import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  Users,
  Search,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  StickyNote,
  Clock,
  CheckCircle,
  Circle,
  Send,
  ArrowLeft,
  BookOpen,
  Eye,
  Trash2,
  MessageCircle,
  HelpCircle,
  Filter,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";

import { API } from "../../utils/api";

const AdminCoaching = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [newNote, setNewNote] = useState("");
  const [newComment, setNewComment] = useState("");
  const [commentTarget, setCommentTarget] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionResponse, setQuestionResponse] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Conversation states
  const [conversations, setConversations] = useState({});
  const [expandedConversations, setExpandedConversations] = useState({});
  const [newConversationMessage, setNewConversationMessage] = useState({});
  const [sendingMessage, setSendingMessage] = useState({});
  // Recent answers tab
  const [activeTab, setActiveTab] = useState("users");
  const [recentAnswers, setRecentAnswers] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchQuestions();
    fetchRecentAnswers();
    // If URL has ?user=xxx, open that user directly
    const userParam = searchParams.get("user");
    if (userParam) {
      setActiveTab("users");
      fetchUserDetail(userParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, statusFilter]);

  const fetchUsers = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/coaching/users`, {
        headers,
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/coaching/questions`, {
        headers,
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setQuestions(data);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  const fetchRecentAnswers = async () => {
    setLoadingRecent(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/coaching/recent-answers?limit=30`, {
        headers,
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRecentAnswers(data);
      }
    } catch (error) {
      console.error("Error fetching recent answers:", error);
    } finally {
      setLoadingRecent(false);
    }
  };


  const filterUsers = () => {
    let filtered = [...users];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter((u) => u.access_status === statusFilter);
    }
    
    setFilteredUsers(filtered);
  };

  const fetchUserDetail = async (userId) => {
    setLoadingDetail(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/coaching/users/${userId}`, {
        headers,
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUserDetail(data);
        setSelectedUser(userId);
      }
    } catch (error) {
      console.error("Error fetching user detail:", error);
      toast.error("Erreur lors du chargement du détail");
    } finally {
      setLoadingDetail(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedUser) return;
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const response = await fetch(`${API}/admin/coaching/users/${selectedUser}/notes`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ content: newNote }),
      });
      
      if (response.ok) {
        toast.success("Note ajoutée");
        setNewNote("");
        fetchUserDetail(selectedUser);
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la note");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/coaching/notes/${noteId}`, {
        method: "DELETE",
        headers,
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Note supprimée");
        fetchUserDetail(selectedUser);
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const addComment = async (targetType, targetId, moduleNumber) => {
    if (!newComment.trim() || !selectedUser) return;
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const response = await fetch(`${API}/admin/coaching/users/${selectedUser}/comments`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          target_type: targetType,
          target_id: targetId,
          module_number: moduleNumber,
          content: newComment,
        }),
      });
      
      if (response.ok) {
        toast.success("Commentaire ajouté (visible par le coaché)");
        setNewComment("");
        setCommentTarget(null);
        fetchUserDetail(selectedUser);
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout du commentaire");
    }
  };

  const respondToQuestion = async (questionId) => {
    if (!questionResponse.trim()) return;
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const response = await fetch(`${API}/admin/coaching/questions/${questionId}/respond`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ response: questionResponse }),
      });
      
      if (response.ok) {
        toast.success("Réponse envoyée par email");
        setQuestionResponse("");
        setSelectedQuestion(null);
        fetchQuestions();
        if (selectedUser) fetchUserDetail(selectedUser);
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la réponse");
    }
  };

  const exportUserPdf = async (userId, userName) => {
    setExportingPdf(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/coaching/users/${userId}/export-pdf`, {
        headers,
        credentials: "include",
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rapport_${userName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Rapport PDF téléchargé");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'export");
      }
    } catch (error) {
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  // Fetch conversation for a specific answer
  const fetchConversation = async (answerId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/answers/${answerId}/conversation`, {
        headers,
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(prev => ({
          ...prev,
          [answerId]: data.messages || []
        }));
        
        // Mark as read
        if (data.unread_count > 0) {
          await fetch(`${API}/answers/${answerId}/conversation/mark-read`, {
            method: "POST",
            headers,
            credentials: "include",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching conversation:", error);
    }
  };

  // Toggle conversation visibility and load if needed
  const toggleConversation = async (answerId) => {
    const isExpanded = expandedConversations[answerId];
    
    if (!isExpanded && !conversations[answerId]) {
      await fetchConversation(answerId);
    }
    
    setExpandedConversations(prev => ({
      ...prev,
      [answerId]: !prev[answerId]
    }));
  };

  // Send message in conversation
  const sendConversationMessage = async (answerId) => {
    const message = newConversationMessage[answerId];
    if (!message?.trim()) return;

    setSendingMessage(prev => ({ ...prev, [answerId]: true }));
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/answers/${answerId}/conversation`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ content: message }),
      });

      if (response.ok) {
        const newMsg = await response.json();
        setConversations(prev => ({
          ...prev,
          [answerId]: [...(prev[answerId] || []), newMsg]
        }));
        setNewConversationMessage(prev => ({ ...prev, [answerId]: "" }));
        toast.success("Message envoyé");
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSendingMessage(prev => ({ ...prev, [answerId]: false }));
    }
  };

  const toggleModule = (moduleNumber) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleNumber]: !prev[moduleNumber],
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const pendingQuestions = questions.filter((q) => q.status === "pending");

  if (selectedUser && userDetail) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 p-6 md:p-8 lg:ml-64 overflow-auto">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedUser(null);
              setUserDetail(null);
            }}
            className="mb-4"
            data-testid="back-to-list-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la liste
          </Button>

          {/* User header */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-[#101E4D]">
                  {userDetail.user.name}
                </h1>
                <p className="text-gray-500">{userDetail.user.email}</p>
                <div className="flex gap-2 mt-2">
                  {userDetail.user.pcm_base && (
                    <Badge className="bg-green-100 text-green-700">
                      Base: {userDetail.user.pcm_base}
                    </Badge>
                  )}
                  {userDetail.user.pcm_phase && (
                    <Badge className="bg-blue-100 text-blue-700">
                      Phase: {userDetail.user.pcm_phase}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => exportUserPdf(userDetail.user.user_id, userDetail.user.name)}
                  disabled={exportingPdf}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                  data-testid="export-pdf-btn"
                >
                  {exportingPdf ? (
                    <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-2" />
                  )}
                  Exporter PDF
                </Button>
                <Badge
                  className={
                    userDetail.user.access_status === "active"
                      ? "bg-green-100 text-green-700"
                      : userDetail.user.access_status === "blocked"
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                  }
                >
                  {userDetail.user.access_status}
                </Badge>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content - Modules & Answers */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-lg font-semibold text-[#101E4D] flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Progression par module
              </h2>

              {userDetail.modules
                .filter((m) => !m.is_annex)
                .map((module) => (
                  <Card key={module.module_number} className="overflow-hidden">
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                      onClick={() => toggleModule(module.module_number)}
                      data-testid={`module-toggle-${module.module_number}`}
                    >
                      <div className="flex items-center gap-3">
                        {module.progress.validation_completed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : module.progress.is_started ? (
                          <Clock className="w-5 h-5 text-orange-500" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300" />
                        )}
                        <div>
                          <p className="font-medium text-[#101E4D]">
                            Module {module.module_number}: {module.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {module.answers_count} réponse(s) enregistrée(s)
                          </p>
                        </div>
                      </div>
                      {expandedModules[module.module_number] ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {expandedModules[module.module_number] && (
                      <div className="border-t bg-gray-50 p-4 space-y-4">
                        {/* Progress info */}
                        <div className="flex gap-4 text-sm">
                          <span
                            className={
                              module.progress.video_watched
                                ? "text-green-600"
                                : "text-gray-400"
                            }
                          >
                            {module.progress.video_watched ? "✓" : "○"} Vidéo vue
                          </span>
                          <span
                            className={
                              module.progress.validation_completed
                                ? "text-green-600"
                                : "text-gray-400"
                            }
                          >
                            {module.progress.validation_completed ? "✓" : "○"} Module
                            validé
                          </span>
                        </div>

                        {/* Answers */}
                        {module.answers.length > 0 ? (
                          <div className="space-y-3">
                            <h4 className="font-medium text-[#101E4D]">
                              Réponses du coaché :
                            </h4>
                            {module.answers.map((answer) => (
                              <div
                                key={answer.answer_id}
                                className="bg-white rounded-lg p-4 border"
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <p className="font-medium text-sm text-orange-600">
                                    {answer.block_info?.title || "Question"}
                                  </p>
                                  <span className="text-xs text-gray-400">
                                    {formatDate(answer.updated_at)}
                                  </span>
                                </div>
                                {answer.block_info?.content && (
                                  <p className="text-xs text-gray-500 mb-2 italic">
                                    "{answer.block_info.content.substring(0, 150)}..."
                                  </p>
                                )}
                                <div className="bg-blue-50 rounded p-3 mt-2">
                                  <p className="text-sm whitespace-pre-wrap">
                                    {answer.answer_text}
                                  </p>
                                </div>

                                {/* Conversation Section */}
                                <div className="mt-3 border-t pt-3">
                                  <button
                                    onClick={() => toggleConversation(answer.answer_id)}
                                    className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#101E4D] transition-colors w-full"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                    <span className="font-medium">
                                      {conversations[answer.answer_id]?.length > 0 
                                        ? `${conversations[answer.answer_id].length} échange(s)` 
                                        : "Échanger avec le coaché"}
                                    </span>
                                    {expandedConversations[answer.answer_id] 
                                      ? <ChevronDown className="w-4 h-4 ml-auto" />
                                      : <ChevronRight className="w-4 h-4 ml-auto" />
                                    }
                                  </button>

                                  {expandedConversations[answer.answer_id] && (
                                    <div className="mt-3 space-y-3">
                                      {/* Messages list */}
                                      {conversations[answer.answer_id]?.length > 0 ? (
                                        <div className="space-y-2 max-h-[250px] overflow-y-auto bg-slate-50 rounded-lg p-3">
                                          {conversations[answer.answer_id].map((msg) => {
                                            const isAdmin = msg.sender_role === "admin";
                                            return (
                                              <div
                                                key={msg.message_id}
                                                className={`flex gap-2 ${isAdmin ? "flex-row-reverse" : ""}`}
                                              >
                                                <div className={`flex-1 max-w-[85%] ${isAdmin ? "text-right" : ""}`}>
                                                  <div
                                                    className={`inline-block px-3 py-2 rounded-lg text-sm ${
                                                      isAdmin
                                                        ? "bg-[#101E4D] text-white"
                                                        : "bg-white border text-slate-700"
                                                    }`}
                                                  >
                                                    {!isAdmin && (
                                                      <p className="text-xs font-semibold text-[#FF6B35] mb-1">
                                                        {msg.sender_name}
                                                      </p>
                                                    )}
                                                    <p className="whitespace-pre-wrap">{msg.content}</p>
                                                  </div>
                                                  <p className="text-xs text-slate-400 mt-0.5">
                                                    {formatDate(msg.created_at)}
                                                  </p>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-slate-400 text-center py-2">
                                          Aucun échange pour le moment
                                        </p>
                                      )}

                                      {/* Input field */}
                                      <div className="flex gap-2 items-end">
                                        <Textarea
                                          placeholder="Votre message..."
                                          value={newConversationMessage[answer.answer_id] || ""}
                                          onChange={(e) => setNewConversationMessage(prev => ({
                                            ...prev,
                                            [answer.answer_id]: e.target.value
                                          }))}
                                          className="min-h-[60px] max-h-[100px] resize-none text-sm flex-1"
                                          data-testid={`conversation-input-${answer.answer_id}`}
                                        />
                                        <Button
                                          onClick={() => sendConversationMessage(answer.answer_id)}
                                          disabled={sendingMessage[answer.answer_id] || !newConversationMessage[answer.answer_id]?.trim()}
                                          size="sm"
                                          className="bg-[#FF6B35] hover:bg-[#E85D2A] h-[60px] px-3"
                                          data-testid={`send-conversation-${answer.answer_id}`}
                                        >
                                          {sendingMessage[answer.answer_id] ? (
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                          ) : (
                                            <Send className="w-4 h-4" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-sm italic">
                            Aucune réponse enregistrée pour ce module
                          </p>
                        )}

                        {/* General comment for module */}
                        {commentTarget === `module-${module.module_number}` ? (
                          <div className="mt-3 space-y-2 bg-white p-3 rounded-lg border">
                            <Textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Commentaire général sur ce module..."
                              className="text-sm"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() =>
                                  addComment("progress", null, module.module_number)
                                }
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Envoyer
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setCommentTarget(null)}
                              >
                                Annuler
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() =>
                              setCommentTarget(`module-${module.module_number}`)
                            }
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            Ajouter un commentaire sur ce module
                          </Button>
                        )}
                      </div>
                    )}
                  </Card>
                ))}

              {/* Coach comments sent */}
              {userDetail.coach_comments.length > 0 && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Commentaires envoyés au coaché
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {userDetail.coach_comments.map((comment) => (
                      <div
                        key={comment.comment_id}
                        className="bg-orange-50 rounded-lg p-3 border-l-4 border-orange-400"
                      >
                        <div className="flex justify-between items-start">
                          <p className="text-sm font-medium text-orange-700">
                            {comment.module_number
                              ? `Module ${comment.module_number}`
                              : "Général"}
                          </p>
                          <span className="text-xs text-gray-400">
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-sm mt-1 whitespace-pre-wrap">
                          {comment.content}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {comment.is_read ? "✓ Lu" : "Non lu"}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Notes & Questions */}
            <div className="space-y-4">
              {/* Private notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Notes privées
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Ajouter une note privée..."
                      className="text-sm"
                      data-testid="new-note-input"
                    />
                    <Button
                      size="sm"
                      onClick={addNote}
                      disabled={!newNote.trim()}
                      data-testid="add-note-btn"
                    >
                      Ajouter
                    </Button>
                  </div>

                  {userDetail.coach_notes.length > 0 ? (
                    <div className="space-y-2 mt-4">
                      {userDetail.coach_notes.map((note) => (
                        <div
                          key={note.note_id}
                          className="bg-yellow-50 rounded-lg p-3 relative group"
                        >
                          <button
                            onClick={() => deleteNote(note.note_id)}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                          <p className="text-sm whitespace-pre-wrap pr-6">
                            {note.content}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            {formatDate(note.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm italic">Aucune note</p>
                  )}
                </CardContent>
              </Card>

              {/* User questions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    Questions du coaché
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {userDetail.questions.length > 0 ? (
                    userDetail.questions.map((question) => (
                      <div
                        key={question.question_id}
                        className={`rounded-lg p-3 border ${
                          question.status === "pending"
                            ? "bg-blue-50 border-blue-200"
                            : "bg-gray-50"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <p className="font-medium text-sm">{question.subject}</p>
                          <Badge
                            variant={
                              question.status === "pending" ? "default" : "secondary"
                            }
                            className="text-xs"
                          >
                            {question.status === "pending"
                              ? "En attente"
                              : "Répondu"}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {question.content}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(question.created_at)}
                        </p>

                        {question.status === "pending" ? (
                          selectedQuestion === question.question_id ? (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                value={questionResponse}
                                onChange={(e) =>
                                  setQuestionResponse(e.target.value)
                                }
                                placeholder="Votre réponse..."
                                className="text-sm"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    respondToQuestion(question.question_id)
                                  }
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  Répondre
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setSelectedQuestion(null)}
                                >
                                  Annuler
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              className="mt-2"
                              onClick={() =>
                                setSelectedQuestion(question.question_id)
                              }
                            >
                              Répondre
                            </Button>
                          )
                        ) : (
                          question.coach_response && (
                            <div className="mt-2 bg-green-50 rounded p-2">
                              <p className="text-xs font-medium text-green-700">
                                Votre réponse :
                              </p>
                              <p className="text-sm">{question.coach_response}</p>
                            </div>
                          )
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm italic">
                      Aucune question posée
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Main list view
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 p-6 md:p-8 lg:ml-64 overflow-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#101E4D]">Suivi Coaching</h1>
          <p className="text-gray-500">
            Suivez la progression et les réponses de vos coachés
          </p>
        </div>

        {/* Pending questions alert */}
        {pendingQuestions.length > 0 && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <HelpCircle className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">
                    {pendingQuestions.length} question(s) en attente de réponse
                  </p>
                  <p className="text-sm text-blue-600">
                    Cliquez sur un coaché pour voir et répondre à ses questions
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 border" data-testid="coaching-tabs">
          <button
            onClick={() => setActiveTab("recent")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "recent"
                ? "bg-[#101E4D] text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            data-testid="tab-recent-answers"
          >
            <MessageSquare className="w-4 h-4" />
            Dernières réponses
            {recentAnswers.length > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === "recent" ? "bg-white/20" : "bg-orange-100 text-orange-700"
              }`}>
                {recentAnswers.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "users"
                ? "bg-[#101E4D] text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4" />
            Coachés
          </button>
        </div>

        {/* === Tab: Recent Answers === */}
        {activeTab === "recent" && (
          <div className="space-y-4" data-testid="recent-answers-section">
            {loadingRecent ? (
              <Card className="p-8 text-center">
                <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-[#FF6B35] rounded-full mx-auto" />
                <p className="mt-2 text-gray-500">Chargement...</p>
              </Card>
            ) : recentAnswers.length === 0 ? (
              <Card className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucune réponse récente</p>
              </Card>
            ) : (
              recentAnswers.map((answer) => (
                <Card
                  key={answer.answer_id}
                  className="hover:shadow-md transition-shadow"
                  data-testid={`recent-answer-${answer.answer_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-sm">
                          {answer.user_name?.charAt(0).toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="font-semibold text-[#101E4D]">{answer.user_name}</p>
                          <p className="text-xs text-gray-500">{answer.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          answer.section === "ancrage" ? "bg-purple-100 text-purple-700" :
                          answer.section === "exercice" ? "bg-blue-100 text-blue-700" :
                          "bg-green-100 text-green-700"
                        }>
                          {answer.section === "ancrage" ? "Ancrage" : answer.section === "exercice" ? "Exercice" : "Contenu"}
                        </Badge>
                        <span className="text-xs text-gray-400">{formatDate(answer.updated_at)}</span>
                      </div>
                    </div>
                    
                    <div className="ml-[52px]">
                      <p className="text-xs text-gray-500 mb-1">
                        Module {answer.module_number} — {answer.module_title} — <span className="font-medium text-[#101E4D]">{answer.block_title}</span>
                      </p>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{answer.answer_text}</p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setActiveTab("users");
                            fetchUserDetail(answer.user_id);
                          }}
                          data-testid={`view-user-${answer.answer_id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Voir le profil
                        </Button>
                        {answer.conversation_count > 0 && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {answer.conversation_count} échange(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* === Tab: Users List === */}
        {activeTab === "users" && (<>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher un coaché..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={statusFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter("all")}
            >
              Tous
            </Button>
            <Button
              variant={statusFilter === "active" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter("active")}
            >
              Actifs
            </Button>
            <Button
              variant={statusFilter === "pending" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter("pending")}
            >
              En attente
            </Button>
          </div>
        </div>

        {/* Users list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-2 text-gray-500">Chargement...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun coaché trouvé</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredUsers.map((user) => (
              <Card
                key={user.user_id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => fetchUserDetail(user.user_id)}
                data-testid={`user-card-${user.user_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold">
                        {user.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[#101E4D]">
                            {user.name}
                          </h3>
                          {user.pending_questions > 0 && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              {user.pending_questions} question(s)
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <div className="flex gap-2 mt-1">
                          {user.pcm_base && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              {user.pcm_base}
                            </span>
                          )}
                          {user.pcm_phase && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              {user.pcm_phase}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-[#101E4D]">
                          {user.progress_percent}%
                        </p>
                        <p className="text-xs text-gray-500">Progression</p>
                        <Progress
                          value={user.progress_percent}
                          className="w-20 h-1 mt-1"
                        />
                      </div>

                      <div className="text-center">
                        <p className="text-lg font-semibold text-[#101E4D]">
                          {user.modules_completed}/6
                        </p>
                        <p className="text-xs text-gray-500">Modules</p>
                      </div>

                      <div className="text-center">
                        <p className="text-lg font-semibold text-[#101E4D]">
                          {user.answers_count}
                        </p>
                        <p className="text-xs text-gray-500">Réponses</p>
                      </div>

                      <Badge
                        className={
                          user.access_status === "active"
                            ? "bg-green-100 text-green-700"
                            : user.access_status === "blocked"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }
                      >
                        {user.access_status}
                      </Badge>

                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>

                  {user.last_activity && (
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Dernière activité : {formatDate(user.last_activity)}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>)}
      </main>
    </div>
  );
};

export default AdminCoaching;
